import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ToolFlows.scss';
import '../../AppContent/NewLandingPage/AddMyPlaceFlow/AddMyPlaceFlow.scss';
import { CloseIcon } from '../CloseIcon';
import ListingPickerList, { formatListingAddress } from './ListingPickerList';
import { SellListing, useSellListings } from '../../../hooks/useSellListings';
import { BuildingWithConvertedAddresses, LatLng } from '../../utils/types';
import {
  isDailyLimitError,
  isUnauthorizedError,
  startPriceEstimationRequest,
} from '../../utils/priceEstimation';
import AddressStep from '../../AppContent/NewLandingPage/AddMyPlaceFlow/steps/AddressStep';
import BuildingConfirmStep from '../../AppContent/NewLandingPage/AddMyPlaceFlow/steps/BuildingConfirmStep';
import SizeStep from '../../AppContent/NewLandingPage/AddMyPlaceFlow/steps/SizeStep';
import RoomsStep from '../../AppContent/NewLandingPage/AddMyPlaceFlow/steps/RoomsStep';
import ReadyToPublishStep from '../../AppContent/NewLandingPage/AddMyPlaceFlow/steps/ReadyToPublishStep';
import {
  AMP_PAYMENT_QUERY_PARAM,
  AMP_RESTORE_STORAGE_KEY,
  AddMyPlaceRestoreState,
} from '../../AppContent/NewLandingPage/AddMyPlaceFlow/AddMyPlaceFlow';
import { ImageLightbox } from '../../ImageLightbox';
import { useListingImageUpload } from '../../../hooks/useListingImageUpload';
import { getCookie } from '../../utils/utils';
import FlowMap from '../../AppContent/NewLandingPage/AddMyPlaceFlow/FlowMap';
import WizardHeader from '../../AppContent/NewLandingPage/AddMyPlaceFlow/WizardHeader';
import { BuildingTypeValue } from '../../AppContent/NewLandingPage/AddMyPlaceFlow/types';
import {
  AddMyPlaceFormState,
  ClickedBuildingState,
  capitalizeAddressParts,
  createInitialFormState,
  deriveDisplay,
  formToEstimationListing,
  resolveBuildingSelection,
} from './estimateWizard';

type EstimateFlowProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Opens the sell flow (unseeded). Used by the empty state AND by the
   * "list it for sale" accept: the flow first writes an AMP restore state, so
   * the sell flow reopens on its publish step with everything prefilled.
   */
  onGoToSell: () => void;
  /** Called after the estimation is queued and the user declines listing. */
  onStarted: () => void;
  /** Seed the wizard map at the user's current landing center. */
  mapCenter: LatLng;
  mapZoom: number;
  countryOfFeedContent: string;
  /** Tools require auth; when false, any start action opens the login modal. */
  isLoggedIn: boolean;
  setLoginModalVisible: (visible: boolean) => void;
};

type Screen = 'choose' | 'wizard' | 'picker' | 'sellPrompt';
type WizardStep = 'address' | 'building' | 'size' | 'rooms' | 'details';

const ADDRESS_PIN_IMG = `${process.env.PUBLIC_URL}/assets/Pin.png`;
const BUILDING_CALLOUT_IMG = `${process.env.PUBLIC_URL}/assets/markers/seller/Home.png`;

// address+building share dot 0, size dot 1, rooms dot 2; details has no dot
// (like AMP's publish step), so its active dot is null.
const STEP_DOT: Record<WizardStep, number | null> = {
  address: 0,
  building: 0,
  size: 1,
  rooms: 2,
  details: null,
};

/**
 * Tools → Estimate price flow (JAA-285). First screen toggles between a new
 * address wizard (reusing the shared AMP steps, including the full sell form
 * with photos + price) and the user's existing listings. Both start an AI
 * price estimation; the wizard path then offers to list the place for sale by
 * handing the collected state to the sell flow via its localStorage restore
 * path, so nothing is re-entered.
 */
const EstimateFlow: React.FC<EstimateFlowProps> = ({
  open,
  onClose,
  onGoToSell,
  onStarted,
  mapCenter,
  mapZoom,
  countryOfFeedContent,
  isLoggedIn,
  setLoginModalVisible,
}) => {
  const { t, i18n } = useTranslation();
  const [screen, setScreen] = useState<Screen>('choose');

  // Wizard state (mirrors the slice of AddMyPlaceFlow the estimate needs).
  const [form, setForm] = useState<AddMyPlaceFormState>(createInitialFormState);
  const [wizardStep, setWizardStep] = useState<WizardStep>('address');
  const [selectedCenter, setSelectedCenter] = useState<LatLng | null>(null);
  const [listingLocation, setListingLocation] = useState<LatLng | null>(null);
  const [clickedBuilding, setClickedBuilding] =
    useState<ClickedBuildingState>(null);
  const [requiresManualBuildingSelection, setRequiresManualBuildingSelection] =
    useState(false);
  const [isAddressFocused, setIsAddressFocused] = useState(false);
  // The details step reuses the sell form (ReadyToPublishStep) with its
  // "Add more details" section open by default — the estimate wants the fields
  // front and center, not behind the toggle.
  const [detailsExpanded, setDetailsExpanded] = useState(true);

  // Shared start state (both paths).
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<
    'daily_limit' | 'generic' | null
  >(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Picker path.
  const { listings, loading, error, fetched, retry } = useSellListings(
    open && screen === 'picker',
  );
  const [pickedListing, setPickedListing] = useState<SellListing | null>(null);

  // Latest-state writer for the AMP restore handoff; assigned below once the
  // image hook provides `images`. A ref so the credits handler (defined first,
  // for the hook's options) can call it without a declaration-order cycle.
  const writeAmpRestoreRef = React.useRef<(() => void) | null>(null);

  // Buying image-enhancement credits mid-flow redirects to Stripe. Save the
  // collected state as an AMP restore first, so the return lands on the sell
  // flow's publish step with everything (incl. photos) intact — same mechanism
  // AMP itself uses for its payment round-trip.
  const handleImageCreditsRequired = useCallback(async () => {
    writeAmpRestoreRef.current?.();
    try {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set(AMP_PAYMENT_QUERY_PARAM, 'credits');
      const returnUrlPath = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
      localStorage.setItem('stripe_return_url', returnUrlPath);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payments/create-checkout-session`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({ return_url: returnUrl.toString() }),
        },
      );
      if (!response.ok) {
        throw new Error(
          `Failed to create checkout session with status ${response.status}`,
        );
      }
      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Error purchasing image credits from estimate flow:', err);
      window.alert(t('building.offMarket.modal.purchaseError'));
    }
  }, [t]);

  const {
    images,
    uploadError,
    isRegenerating,
    enhancementStatus,
    fetchEnhancementStatus,
    uploadFiles,
    removeImage,
    reorderImages,
    toggleImageVersion,
    regenerateImage,
    resetImages,
  } = useListingImageUpload({ onCreditsRequired: handleImageCreditsRequired });

  writeAmpRestoreRef.current = () => {
    const restore: AddMyPlaceRestoreState = {
      step: 'publish',
      form,
      selectedCenter,
      listingLocation,
      clickedBuilding,
      requiresManualBuildingSelection,
      isDetailsExpanded: true,
      images: images
        .filter(
          (image) =>
            !image.isUploading &&
            !image.error &&
            !image.url?.startsWith('blob:'),
        )
        .map((image) => ({ ...image, isUploading: false, isEnhancing: false })),
      enteredFromBuildingView: false,
      savedAt: Date.now(),
    };
    localStorage.setItem(AMP_RESTORE_STORAGE_KEY, JSON.stringify(restore));
  };

  // Fresh enhancement-credit count whenever the wizard opens for a logged-in
  // user (mirrors AMP), so the photo lightbox shows an up-to-date "X left".
  useEffect(() => {
    if (open && isLoggedIn) {
      fetchEnhancementStatus();
    }
  }, [open, isLoggedIn, fetchEnhancementStatus]);

  // Reset everything per open so a reopened flow starts on the choose screen.
  useEffect(() => {
    if (!open) {
      setScreen('choose');
      setForm(createInitialFormState());
      setWizardStep('address');
      setSelectedCenter(null);
      setListingLocation(null);
      setClickedBuilding(null);
      setRequiresManualBuildingSelection(false);
      setStarting(false);
      setStartError(null);
      setPickedListing(null);
      setDetailsExpanded(true);
      setLightboxIndex(null);
      resetImages();
    }
  }, [open, resetImages]);

  // Single listing skips the picker rows. Gated on `fetched` so a reopened
  // flow never auto-selects the previous open's stale listing.
  useEffect(() => {
    if (
      open &&
      screen === 'picker' &&
      fetched &&
      !loading &&
      !error &&
      listings.length === 1 &&
      !pickedListing
    ) {
      setPickedListing(listings[0]);
    }
  }, [open, screen, fetched, loading, error, listings, pickedListing]);

  if (!open) return null;

  const updateForm = (patch: Partial<AddMyPlaceFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const display = deriveDisplay(form);
  const effectiveCenter = selectedCenter ?? mapCenter;

  const handleSelectBuilding = (building: BuildingWithConvertedAddresses) => {
    const selection = resolveBuildingSelection(building);
    updateForm(selection.formPatch);
    if (selection.listingLocation)
      setListingLocation(selection.listingLocation);
    if (selection.clickedBuilding)
      setClickedBuilding(selection.clickedBuilding);
  };

  // Tools require an authenticated session. Not logged in → prompt login and
  // close the flow instead of firing a request that would 401.
  const requireLogin = (): boolean => {
    if (isLoggedIn) return true;
    onClose();
    setLoginModalVisible(true);
    return false;
  };

  const startEstimation = async (
    listing: Parameters<typeof startPriceEstimationRequest>[0],
  ) => {
    setStarting(true);
    setStartError(null);
    try {
      await startPriceEstimationRequest(listing, i18n.language);
      return true;
    } catch (err) {
      // A 401 means the session expired mid-flow — send them to login rather
      // than the generic "try again" error.
      if (isUnauthorizedError(err)) {
        onClose();
        setLoginModalVisible(true);
        return false;
      }
      setStartError(isDailyLimitError(err) ? 'daily_limit' : 'generic');
      setStarting(false);
      return false;
    }
  };

  const handleWizardEstimate = async () => {
    if (starting) return;
    if (!requireLogin()) return;
    const ok = await startEstimation(
      formToEstimationListing(form, clickedBuilding, listingLocation),
    );
    if (ok) {
      setStarting(false);
      setScreen('sellPrompt');
    }
  };

  const handlePickerEstimate = async () => {
    if (!pickedListing || starting) return;
    if (!requireLogin()) return;
    const ok = await startEstimation(pickedListing);
    if (ok) onStarted();
  };

  // "List it for sale too?" accepted: persist the collected state (form,
  // building, photos, price) as an AMP restore, then open the sell flow — it
  // reopens directly on its publish step with everything prefilled.
  const handleListForSale = () => {
    writeAmpRestoreRef.current?.();
    onGoToSell();
  };

  // --- Wizard screen: AMP chrome + shared steps (no AMP file edits) ----------
  if (screen === 'wizard') {
    const isBuildingStep = wizardStep === 'building';
    const showFlowMap = wizardStep === 'address' || isBuildingStep;
    const isBuildingConfirmed =
      !requiresManualBuildingSelection || clickedBuilding !== null;
    const isSizeValid =
      display.displaySize !== '' && Number(display.displaySize) > 0;
    const activeDotDone =
      (wizardStep === 'building' && isBuildingConfirmed) ||
      (wizardStep === 'size' && isSizeValid) ||
      (wizardStep === 'rooms' && display.displayRooms !== '');

    const selectedBuildingLocation = clickedBuilding ? listingLocation : null;
    const buildingHighlightLocation = requiresManualBuildingSelection
      ? selectedBuildingLocation
      : effectiveCenter;

    let stepNode: React.ReactNode = null;
    if (wizardStep === 'address') {
      stepNode = (
        <AddressStep
          value={form.address}
          onChange={(value) => updateForm({ address: value })}
          onSelect={(
            value,
            center,
            postal,
            needsManualBuildingSelection = false,
          ) => {
            updateForm({
              address: capitalizeAddressParts(value),
              postal: postal ?? '',
            });
            if (center) {
              setSelectedCenter(center);
              setListingLocation(center);
            }
            setClickedBuilding(null);
            setRequiresManualBuildingSelection(needsManualBuildingSelection);
            setWizardStep('building');
          }}
          onFocusedChange={setIsAddressFocused}
          searchCenter={effectiveCenter}
        />
      );
    } else if (wizardStep === 'building') {
      stepNode = (
        <BuildingConfirmStep
          addressWithPostal={display.addressWithPostal}
          buildingType={form.buildingType}
          onBuildingTypeChange={(value: BuildingTypeValue) =>
            updateForm({ buildingType: value })
          }
          onNext={() => setWizardStep('size')}
          requiresManualBuildingSelection={requiresManualBuildingSelection}
          hasSelectedBuilding={clickedBuilding !== null}
        />
      );
    } else if (wizardStep === 'size') {
      stepNode = (
        <SizeStep
          address={display.displayAddress}
          area={display.previewCity}
          size={form.size}
          onSizeChange={(value) => updateForm({ size: value })}
          onNext={() => setWizardStep('rooms')}
        />
      );
    } else if (wizardStep === 'rooms') {
      stepNode = (
        <RoomsStep
          address={display.displayAddress}
          area={display.previewCity}
          size={display.displaySize}
          rooms={form.rooms}
          onSelectRooms={(value) => updateForm({ rooms: value })}
          onNext={() => setWizardStep('details')}
        />
      );
    } else {
      stepNode = (
        <ReadyToPublishStep
          form={form}
          displayAddress={display.displayAddress}
          previewCity={display.previewCity}
          addressWithPostal={display.addressWithPostal}
          displaySize={display.displaySize}
          displayRooms={display.displayRooms}
          onBuildingTypeChange={(value: BuildingTypeValue) =>
            updateForm({ buildingType: value })
          }
          onFormChange={updateForm}
          onPublish={handleWizardEstimate}
          isPublishing={starting}
          detailsExpanded={detailsExpanded}
          onDetailsExpandedChange={setDetailsExpanded}
          photos={images}
          uploadError={uploadError}
          onUploadPhotos={uploadFiles}
          onRemovePhoto={removeImage}
          onReorderPhotos={reorderImages}
          onOpenPhotoManager={setLightboxIndex}
          isLoggedIn={isLoggedIn}
          setLoginModalVisible={setLoginModalVisible}
          ctaLabel={t('newLandingPage.toolFlows.estimateCtaGet')}
          title={t('newLandingPage.toolFlows.formTitle')}
          detailsHint={t('newLandingPage.toolFlows.addDetailsHint')}
          hidePrice
          showEstimateNotify={false}
        />
      );
    }

    const handleWizardBack = () => {
      if (wizardStep === 'building') setWizardStep('address');
      else if (wizardStep === 'size') setWizardStep('building');
      else if (wizardStep === 'rooms') setWizardStep('size');
      else if (wizardStep === 'details') setWizardStep('rooms');
      else setScreen('choose');
    };

    const isDetailsStep = wizardStep === 'details';
    const flowCardClass = isDetailsStep
      ? `amp-flow amp-flow--publish${detailsExpanded ? ' amp-flow--details-expanded' : ''}`
      : 'amp-flow amp-flow--limited-height';

    return (
      <div className="amp-flow__overlay" data-open="true">
        <div className={flowCardClass} role="dialog" aria-modal="true">
          <WizardHeader
            hideBack={false}
            onBack={handleWizardBack}
            onClose={onClose}
            activeDot={STEP_DOT[wizardStep]}
            activeDotDone={activeDotDone}
            title={t('newLandingPage.tools.estimate.title')}
          />
          <div
            className={`amp-flow__content amp-flow__content--${wizardStep}`}
            data-address-focused={
              wizardStep === 'address' && isAddressFocused ? 'true' : 'false'
            }
          >
            {stepNode}
            {showFlowMap && (
              <FlowMap
                key="estimate-shared-map"
                mapCenter={effectiveCenter}
                mapZoom={mapZoom}
                countryOfFeedContent={countryOfFeedContent}
                recenterInstant
                disableBuildingClick={!isBuildingStep}
                staticMap={!isBuildingStep}
                hideLabels={!isBuildingStep}
                selectBuilding={
                  isBuildingStep ? handleSelectBuilding : undefined
                }
                highlightAddressAt={
                  isBuildingStep ? buildingHighlightLocation : null
                }
                addressCallout={
                  isBuildingStep && buildingHighlightLocation !== null ? (
                    <div className="amp-building__callout">
                      <img
                        src={BUILDING_CALLOUT_IMG}
                        alt=""
                        aria-hidden="true"
                        className="amp-building__callout-house"
                      />
                      <div className="amp-building__callout-text">
                        <span className="amp-building__callout-title">
                          {t('newLandingPage.addMyPlace.building.calloutTitle')}
                        </span>
                        <span className="amp-building__callout-address">
                          {display.displayAddress}
                        </span>
                      </div>
                    </div>
                  ) : undefined
                }
                centerOverlay={
                  !isBuildingStep ? (
                    <div className="amp-map-pin">
                      <img
                        src={ADDRESS_PIN_IMG}
                        alt=""
                        aria-hidden="true"
                        className="amp-map-pin__house"
                      />
                    </div>
                  ) : undefined
                }
              />
            )}
          </div>
          {startError === 'generic' && (
            <p className="tool-flow__state-text">
              {t('newLandingPage.toolFlows.startError')}
            </p>
          )}
          {startError === 'daily_limit' && (
            <p className="tool-flow__state-text">
              {t('newLandingPage.toolFlows.dailyLimit')}
            </p>
          )}
        </div>
        <ImageLightbox
          isOpen={lightboxIndex !== null && images.length > 0}
          images={images}
          initialIndex={lightboxIndex ?? 0}
          onClose={() => setLightboxIndex(null)}
          onToggleVersion={toggleImageVersion}
          onRegenerate={regenerateImage}
          isRegenerating={isRegenerating}
          enhancementCredits={enhancementStatus?.credits ?? null}
          onEnhance={regenerateImage}
          onBuyCredits={handleImageCreditsRequired}
        />
      </div>
    );
  }

  // --- Non-wizard screens use the tool-flow panel chrome ---------------------
  const header = (title: string) => (
    <div className="tool-flow__header">
      <h2 className="tool-flow__title">{title}</h2>
      <button
        type="button"
        className="tool-flow__close"
        aria-label={t('newLandingPage.tools.closeAria')}
        onClick={onClose}
      >
        <CloseIcon size={18} color="#131c20" />
      </button>
    </div>
  );

  return (
    <div
      className="tool-flow"
      role="dialog"
      aria-modal="true"
      aria-label={t('newLandingPage.tools.estimate.title')}
    >
      <div className="tool-flow__backdrop" onClick={onClose} />
      <div className="tool-flow__panel">
        {header(t('newLandingPage.tools.estimate.title'))}

        {screen === 'choose' && (
          <div className="tool-flow__choose">
            <button
              type="button"
              className="tool-flow__cta"
              onClick={() => setScreen('wizard')}
            >
              {t('newLandingPage.toolFlows.chooseNewAddress')}
            </button>
            <button
              type="button"
              className="tool-flow__cta tool-flow__cta--secondary"
              onClick={() => setScreen('picker')}
            >
              {t('newLandingPage.toolFlows.chooseFromListings')}
            </button>
          </div>
        )}

        {screen === 'picker' && (
          <>
            {loading && (
              <div className="tool-flow__state">
                <p className="tool-flow__state-text">
                  {t('newLandingPage.toolFlows.loading')}
                </p>
              </div>
            )}
            {!loading && error && (
              <div className="tool-flow__state">
                <p className="tool-flow__state-text">
                  {t('newLandingPage.toolFlows.error')}
                </p>
                <button
                  type="button"
                  className="tool-flow__cta"
                  onClick={retry}
                >
                  {t('newLandingPage.toolFlows.retry')}
                </button>
              </div>
            )}
            {!loading && !error && listings.length === 0 && (
              <div className="tool-flow__state">
                <p className="tool-flow__state-text">
                  {t('newLandingPage.toolFlows.empty.text')}
                </p>
                <button
                  type="button"
                  className="tool-flow__cta"
                  onClick={onGoToSell}
                >
                  {t('newLandingPage.toolFlows.empty.cta')}
                </button>
              </div>
            )}
            {!loading && !error && listings.length > 1 && !pickedListing && (
              <>
                <p className="tool-flow__meta">
                  {t('newLandingPage.toolFlows.pickerHint')}
                </p>
                <ListingPickerList
                  listings={listings}
                  onSelect={setPickedListing}
                />
              </>
            )}
            {pickedListing && (
              <div className="tool-flow__state">
                <p className="tool-flow__state-text">
                  {[
                    formatListingAddress(pickedListing).line1,
                    formatListingAddress(pickedListing).line2,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                {startError === 'daily_limit' && (
                  <p className="tool-flow__state-text">
                    {t('newLandingPage.toolFlows.dailyLimit')}
                  </p>
                )}
                {startError === 'generic' && (
                  <p className="tool-flow__state-text">
                    {t('newLandingPage.toolFlows.startError')}
                  </p>
                )}
                {startError !== 'daily_limit' && (
                  <button
                    type="button"
                    className="tool-flow__cta"
                    onClick={handlePickerEstimate}
                    disabled={starting}
                  >
                    {starting
                      ? t('newLandingPage.toolFlows.starting')
                      : t('newLandingPage.toolFlows.start')}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {screen === 'sellPrompt' && (
          <div className="tool-flow__state">
            <p className="tool-flow__state-text">
              {t('newLandingPage.toolFlows.sellPromptTitle')}
            </p>
            <button
              type="button"
              className="tool-flow__cta"
              onClick={handleListForSale}
            >
              {t('newLandingPage.toolFlows.sellPromptYes')}
            </button>
            <button
              type="button"
              className="tool-flow__cta tool-flow__cta--secondary"
              onClick={onStarted}
            >
              {t('newLandingPage.toolFlows.sellPromptNo')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EstimateFlow;
